//////////////////////////////////////////////////////////////////////////////
//
//  Token Refresh Handler
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: Token 갱신 및 재시도 로직
//       - Queue 기반 재시도 (여러 요청 동시 처리)
//       - Token 만료 감지
//       - 에러 처리 및 사용자 알림
//
//////////////////////////////////////////////////////////////////////////////

import axios, { AxiosInstance } from 'axios';
import { showErrorAlert } from '../utils/errorHandler';
import { ERROR_ALERT_TYPE } from '../constants/ApiConstants';
import logger from '../utils/logger';
import {
  getStoredAccessToken,
  getRefreshToken,
  getKeyCredential,
  storeUserCredential,
  clearUserCredential,
} from 'Shared/Utils/CredentialStorage';

import { getBaseUrl as getApiBaseUrl } from '../utils/urlBuilder';

// API Base URL from environment variable (urlBuilder와 동일한 함수 사용)
const getBaseUrl = (): string => {
  return getApiBaseUrl();
};

////////////////////////////////////////////////////////////////////////////////////
// Token Refresh State
////////////////////////////////////////////////////////////////////////////////////

// Shared Refresh Promise (Singleton)
let refreshPromise: Promise<string | null> | null = null;
let isSessionExpired = false;

/**
 * Token 갱신 중인지 여부 확인 (Getter)
 */
export const getIsRefreshing = () => refreshPromise !== null;

/**
 * 세션 만료 상태 설정 (로그아웃 시 사용)
 */
export const setSessionExpired = (expired: boolean) => {
  isSessionExpired = expired;
};

/**
 * 진행 중인 Refresh Promise 반환 (Request Interceptor용)
 */
export const getRefreshPromise = () => refreshPromise;

/**
 * Token 만료 여부 확인
 */
export const isTokenExpired = (error: any): boolean => {
  const authInfo = error.response?.headers?.['www-authenticate'];
  if (!authInfo) return false;

  const authString = authInfo.toString();
  return (
    authString.indexOf('token expired') > 0 &&
    authString.indexOf('invalid_token') > 0
  );
};

/**
 * Token 갱신 및 원래 요청 재시도
 */
/**
 * Token 갱신 프로세스 시작 (또는 진행 중인 Promise 반환)
 */
export const initiateTokenRefresh = async (): Promise<string | null> => {
  if (isSessionExpired) {
    return null;
  }

  // Refresh Promise가 없으면 생성
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const refreshToken = await getRefreshToken();
        const userId = await getKeyCredential();

        if (!refreshToken || !userId) {
          throw new Error('Missing refresh credentials');
        }

        logger.info('🔄 Refreshing token...', { userId });

        const uri = `${getBaseUrl()}/Authentication/RefreshToken`;
        const refreshResponse = await axios.post(uri, {
          Id: userId,
          GrantType: 'refresh',
          RefreshToken: refreshToken,
          Password: "",
        });

        if (
          refreshResponse?.status === 200 &&
          refreshResponse.data?.token
        ) {
          const newAccessToken = refreshResponse.data.token;
          const newRefreshToken =
            refreshResponse.data.refreshToken ||
            refreshResponse.data.refresh_token ||
            refreshToken;

          await storeUserCredential({
            token: newAccessToken,
            refreshToken: newRefreshToken,
            userId: userId,
          });

          logger.info('✅ Token refreshed successfully');
          return newAccessToken as string;
        } else {
          logger.error('❌ Refresh token response is invalid', refreshResponse);
          throw new Error('Invalid refresh response');
        }
      } catch (err: any) {
        logger.error('❌ Refresh token request failed', err);
        const status = err.response?.status;

        // 400 (Invalid Grant), 401 (Unauthorized), 403 (Forbidden) -> Session Expired
        if (status === 400 || status === 401 || status === 403) {
          if (!isSessionExpired) {
            // 이미 로그인 페이지에 있다면 알림/리다이렉트 생략
            if (window.location.pathname.includes('/authentication/sign-in') || window.location.pathname.includes('/login')) {
              isSessionExpired = true; // 상태만 변경하고 넘어감
              return null;
            }

            isSessionExpired = true;
            await clearUserCredential();
            alert('세션이 만료되었습니다. 다시 로그인해주세요.');
            window.location.href = '/authentication/sign-in/basic';
          }
        }
        // 5xx (Server Error) or Network Error -> Keep Session, Just Fail Refresh
        else {
          logger.warn('⚠️ Server/Network error during refresh - Session preserved', err.message);
          // Optional: Show toast/alert "Connection failed, please try again" if needed, 
          // but often silent fail or retry is better for UX if it's transient.
          // Considering User Request: "Server shutdown -> refresh failed -> session expired".
          // We FIXED the expiration part. 
          // We might want to alert if it's a manual action, but for auto-refresh background, 
          // maybe just log is enough or small toast. 
          // I'll stick to logging to avoid spamming alerts on every request if server is down.
        }

        return null;
      } finally {
        setTimeout(() => {
          refreshPromise = null;
        }, 100);
      }
    })();
  }

  return refreshPromise;
};

/**
 * Token 갱신 및 원래 요청 재시도
 */
export const handleTokenRefresh = async (
  error: any,
  axiosInstance: AxiosInstance,
) => {
  // 이미 세션이 만료된 경우
  if (isSessionExpired) {
    return Promise.reject({
      ...error,
      refreshFailed: true,
      message: 'Session already expired',
    });
  }

  try {
    const { response: errorResponse } = error;

    // Refresh 시작 (또는 대기)
    const newAccessToken = await initiateTokenRefresh();

    if (newAccessToken) {
      // Retry with new token
      if (errorResponse.config.headers) {
        errorResponse.config.headers.Authorization = 'Bearer ' + newAccessToken;
      }
      return axiosInstance(errorResponse.config);
    } else {
      // Refresh failed
      return Promise.reject({
        ...error,
        refreshFailed: true,
        message: 'Token refresh failed',
      });
    }
  } catch (err) {
    return Promise.reject(err);
  }
};

////////////////////////////////////////////////////////////////////////////////////
// Refresh Error Handler
////////////////////////////////////////////////////////////////////////////////////

/**
 * Token Refresh 에러 처리
 */
const handleRefreshError = async (error: any) => {
  if (error.response) {
    const status = error.response.status;

    if (status === 401 || status === 403) {
      // ✅ Token 만료 - 로그아웃 처리
      logger.error('❌ Refresh Token 만료 (401/403) - 로그아웃 처리');

      if (!isSessionExpired) {
        // 이미 로그인 페이지에 있다면 알림/리다이렉트 생략
        if (window.location.pathname.includes('/authentication/sign-in') || window.location.pathname.includes('/login')) {
          isSessionExpired = true; // 상태만 변경하고 넘어감
          return;
        }

        isSessionExpired = true;
        // 사용자에게 알림 (리다이렉트 전에)
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');

        await clearUserCredential();
        window.location.href = '/authentication/sign-in/basic';
      }
    } else if (status >= 500) {
      // 서버 에러 (credential 유지)
      showErrorAlert(ERROR_ALERT_TYPE.SERVER_ERROR);
    } else {
      // 기타 HTTP 에러 (credential 유지)
      showErrorAlert(ERROR_ALERT_TYPE.HTTP_ERROR);
    }
  } else if (
    error.code === 'NETWORK_ERROR' ||
    error.message === 'Network Error' ||
    error.code === 'ECONNABORTED'
  ) {
    // 네트워크 에러 (credential 유지)
    showErrorAlert(ERROR_ALERT_TYPE.NETWORK_ERROR);
  } else {
    // 알 수 없는 에러 (credential 유지)
    logger.error('Unknown refresh error', error);
  }
};

export default handleTokenRefresh;

